import {
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import styles from './RangoInputCode.module.css'

type Props = {
  /** Rótulo visível do campo (obrigatório: o DS proíbe só placeholder). */
  label: ReactNode
  value: string
  onChange: (value: string) => void
  /** Quantidade de itens (`Items`) do Input Code. */
  length?: number
  id?: string
  name?: string
  supportText?: ReactNode
  error?: ReactNode
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  /** Oculta os dígitos (variante "Preenchido oculto"). */
  masked?: boolean
  onComplete?: (value: string) => void
  autoComplete?: string
  inputMode?: 'numeric' | 'text'
  className?: string
}

/**
 * Input Code Rango DS — código numérico segmentado (OTP).
 *
 * Acessibilidade: um único `<input>` real recebe o valor (teclado virtual,
 * autofill `one-time-code` e leitores de tela funcionam como num input
 * comum); os segmentos visuais são `aria-hidden` e derivados do valor.
 */
export function RangoInputCode({
  label,
  value,
  onChange,
  length = 4,
  id,
  name,
  supportText,
  error,
  disabled,
  required,
  autoFocus,
  masked = false,
  onComplete,
  autoComplete = 'one-time-code',
  inputMode = 'numeric',
  className,
}: Props) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const labelId = `${fieldId}-label`
  const errorId = error ? `${fieldId}-error` : undefined
  const supportId = supportText ? `${fieldId}-support` : undefined
  const describedBy =
    [errorId, supportId].filter(Boolean).join(' ') || undefined
  const inputRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)
  // Valor pintado nos segmentos. Normalmente espelha `value`; no paste
  // atualizamos na hora para não esperar o `value` controlado do React.
  const [painted, setPainted] = useState(value)

  useEffect(() => {
    setPainted(value)
  }, [value])

  const digits = painted.slice(0, length)
  const activeIndex = Math.min(digits.length, length - 1)
  const filled = digits.length === length

  function commit(next: string): string {
    const clean = next.replace(/\D/g, '').slice(0, length)
    setPainted(clean)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
    return clean
  }

  /** Colar substitui o código inteiro (o valor é cortado em `length`). */
  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData('text')
    if (!text) return
    event.preventDefault()
    commit(text)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return
    }
    if (event.key === 'Backspace' && !digits) {
      event.preventDefault()
    }
  }

  return (
    <div
      className={[styles.field, className].filter(Boolean).join(' ')}
      data-disabled={disabled ? 'true' : undefined}
    >
      <label
        id={labelId}
        className={styles.label}
        htmlFor={fieldId}
        data-required={required ? 'true' : undefined}
      >
        {label}
      </label>
      <div
        className={styles.boxes}
        data-focused={focused ? 'true' : undefined}
        data-error={error ? 'true' : undefined}
        onClick={() => inputRef.current?.focus()}
      >
        <div className={styles.segments} aria-hidden="true" data-masked={masked ? 'true' : undefined}>
          {Array.from({ length }, (_, index) => {
            const char = digits[index]
            const isActive = focused && index === activeIndex && !filled
            const filledSegment = Boolean(char)
            return (
              <span
                key={index}
                className={styles.segment}
                data-state={
                  filledSegment ? 'filled' : isActive ? 'active' : 'default'
                }
                data-testid={`input-code-segment-${index}`}
              >
                {char ? (masked ? '•' : char) : ''}
              </span>
            )
          })}
        </div>
        <input
          ref={inputRef}
          id={fieldId}
          name={name}
          className={styles.input}
          role="textbox"
          aria-roledescription="código de verificação"
          value={digits}
          onChange={(e) => commit(e.target.value)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputMode={inputMode}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          required={required}
          maxLength={length}
          aria-labelledby={labelId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          data-testid="input-code-input"
        />
      </div>
      {error ? (
        <p id={errorId} className={styles.codeError} role="alert">
          {error}
        </p>
      ) : null}
      {supportText ? (
        <p id={supportId} className={styles.codeSupport}>
          {supportText}
        </p>
      ) : null}
    </div>
  )
}
